import React, {useEffect, useMemo, useRef, useState} from 'react';
import SignPad from 'signature_pad';
import {
  SignaturePadWrapper,
  PreviewButton,
  Button,
  ButtonContainer,
  Dialog,
  DialogContent,
  Placeholder,
  Header,
  Title,
  CloseButton,
  CanvasContainer,
  Pad,
  ClearButton,
  ColorsContainer,
  ColorButton,
  COLORS
} from './styles';
import {clamp} from 'lodash';
import messages from './messages';

export default function SignaturePad({signature, onSubmit, formatMessage}) {
  const signaturePad = useRef(null);
  const previewButtonRef = useRef(null);
  const [previewButtonSize, setPreviewButtonSize] = useState();
  const [selectedColor, setSelectedColor] = useState(COLORS.black);
  const [dialogOpen, setDialogOpen] = useState(false);

  const canvasSize = useMemo(() => {
    const width = clamp((2 * window.innerWidth) / 3, 360, 720);
    const height = width / 2;
    return {width, height};
  }, []);

  useEffect(() => {
    setPreviewButtonSize(previewButtonRef.current.getBoundingClientRect());
  }, []);

  useEffect(() => {
    if (!dialogOpen) return;
    const canvas = document.getElementById('signature-pad');
    signaturePad.current = new SignPad(canvas, {throttle: 0});

    if (signature.points) {
      const tmpRatio = window.devicePixelRatio;
      window.devicePixelRatio = 1;
      signaturePad.current.fromData(signature.points);
      window.devicePixelRatio = tmpRatio;
    }
  }, [signature.data, dialogOpen]);

  const getSignature = e => {
    e.preventDefault();
    if (signaturePad.current.isEmpty()) {
      onSubmit(undefined);
    } else {
      const tmpRatio = window.devicePixelRatio;
      window.devicePixelRatio = 1;
      const data = signaturePad.current.toDataURL('image/svg+xml');
      const svg = signaturePad.current.toSVG({includeBackgroundColor: true});
      const points = signaturePad.current.toData();
      onSubmit({data, points, svg});
      window.devicePixelRatio = tmpRatio;
    }
    closeModal(e);
  };

  const clearSignature = e => {
    e.preventDefault();
    signaturePad.current.clear();
  };

  const closeModal = e => {
    e.preventDefault();
    setDialogOpen(false);
    window['signature-dialog'].close();
  };

  const onCancel = e => {
    e.preventDefault();
    closeModal(e);
  };

  const openModal = e => {
    e.preventDefault();
    setDialogOpen(true);
    setSelectedColor(COLORS.black);
    window['signature-dialog'].showModal();
  };

  const setColor = color => {
    setSelectedColor(color);
    signaturePad.current.penColor = color;
  };

  return (
    <SignaturePadWrapper style={{display: 'flex', flex: 1}}>
      <PreviewButton
        ref={previewButtonRef}
        onClick={openModal}
        maxHeight={previewButtonSize?.height}
        maxWidth={previewButtonSize?.width}
      >
        {signature.data ? (
          <img src={signature.data} width={previewButtonSize?.width} height={previewButtonSize?.height} />
        ) : (
          <Placeholder>{formatMessage(messages.clickToSign)}</Placeholder>
        )}
      </PreviewButton>

      <Dialog id="signature-dialog" onClick={closeModal}>
        <DialogContent
          canvasSize={canvasSize}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {dialogOpen && (
            <>
              <Header>
                <Title>{formatMessage(messages.signature)}</Title>
                <CloseButton onClick={closeModal} />
              </Header>
              <CanvasContainer width={canvasSize.width} height={canvasSize.height}>
                <Pad id="signature-pad" width={canvasSize.width} height={canvasSize.height} />
                <ClearButton onClick={clearSignature}>{formatMessage(messages.clear)}</ClearButton>
                <ColorsContainer>
                  {Object.values(COLORS).map(color => (
                    <ColorButton
                      key={color}
                      color={color}
                      onClick={() => setColor(color)}
                      selected={selectedColor === color}
                    />
                  ))}
                </ColorsContainer>
              </CanvasContainer>
              <ButtonContainer>
                <Button onClick={onCancel} secondary>
                  {formatMessage(messages.cancel)}
                </Button>
                <Button onClick={getSignature}>{formatMessage(messages.acceptAndSign)}</Button>
              </ButtonContainer>
            </>
          )}
        </DialogContent>
      </Dialog>
    </SignaturePadWrapper>
  );
}
